import { $getRoot, createEditor, CreateEditorArgs } from 'lexical';
import { defaultConfig } from '@/components/LexicalEditor/LexicalContext';
import { PLAYGROUND_TRANSFORMERS } from '@/components/LexicalEditor/plugins/MarkdownTransformers';
import { $convertFromMarkdownString } from '@lexical/markdown';
import {
  getPrevPageArea,
  useGTMEventTracker,
} from '@/hooks/gtm/useGTMEventTracker';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { RecordFilter } from '@/types/api';
import removeMarkdown from 'remove-markdown';
import { GTMEventName } from '@/constant/gtm';
import { getDocumentDetailParam } from '@/utils/gtm/work';
import InsertToBottomIcon from '@/public/icons/document/insert-to-bottom.svg';
import ReplaceIcon from '@/public/icons/document/replace.svg';
import CopyIcon from '@/public/icons/document/copy.svg';
import { useCallback, useState } from 'react';
import useAi from '@/components/Record/hooks/useAi';
import {
  AiSummarizedContent,
  DocumentAiSummariesState,
} from '@/recoil/atom/documents';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  deleteTalkFlowDocumentAiSummary,
  useTalkFlowDocumentAiSummary,
} from '@/hooks/apiHooks';
import { MyInfoState } from '@/recoil/atom';

const BLOCK_TAG_REGEX = /<(p|h[1-6]|ul|ol|li|table|blockquote|pre|div)[\s/>]/i;

// content가 블록 HTML이 아니면(과거 버전에서 마크다운 원문이 저장된 문서 등)
// 에디터 로드 시 root에 인라인 노드를 붙이다 장애가 나므로 HTML로 변환한다
export async function ensureLexicalHtml(content: string): Promise<string> {
  if (!content || BLOCK_TAG_REGEX.test(content)) {
    return content;
  }
  return getLexicalNodesFromMarkdown(content);
}

export async function getLexicalNodesFromMarkdown(
  markdownText: string,
): Promise<string> {
  const container = document.createElement('div');
  document.body.appendChild(container);

  // 간단한 정규화: NBSP -> space, tab -> 4 spaces, 2-space indent -> 4-space
  function normalizeIndentation(md: string) {
    let s = md.replace(/\u00A0/g, ' ');
    s = s.replace(/\t/g, '    ');
    s = s.replace(/^(\s{2})([-*+] )/gm, '    $2');
    return s;
  }

  const normalized = normalizeIndentation(markdownText);
  const tempEditor = createEditor(defaultConfig as unknown as CreateEditorArgs);
  tempEditor.setRootElement(container); //변환 전에 루트 엘리먼트를 설정(중요!)

  //변환을 update() 내부에서 수행하고, 완료될 때까지 await
  tempEditor.update(() => {
    const root = $getRoot();
    $convertFromMarkdownString(normalized, PLAYGROUND_TRANSFORMERS, root);
  });

  // DOM이 반영될 시간을 한 프레임 확보
  await new Promise((resolve) => setTimeout(resolve, 0));

  preserveTextDecorationFormats(container);
  const html = container.innerHTML;
  container.remove();
  return html;
}

/*
 * 취소선·밑줄을 다시 읽을 수 있는 태그로 감싼다.
 *
 * Lexical 이 화면에 그리는 DOM(createDOM)은 굵게=<strong>, 기울임=<em> 처럼 전용 태그를 주지만
 * 취소선·밑줄은 **테마 클래스만** 붙인 <span> 이다. 그런데 이 HTML 을 다시 에디터로 불러올 때
 * <span> 은 클래스를 보지 않고 인라인 style(text-decoration)만 보므로 서식이 통째로 사라진다.
 * (AI Chat 답변 → 'AI 정리' 덮어쓰기에서 굵게·기울임만 남고 취소선이 없어지던 원인)
 *
 * 저장 HTML 은 뷰어들이 PlaygroundEditorTheme__* 클래스로 스타일링하므로 클래스는 그대로 두고,
 * 바깥에 <s>/<u> 만 덧씌운다 — 둘 다 Lexical 기본 importDOM 이 아는 태그다.
 */
const DECORATION_CLASS = {
  strikethrough: 'PlaygroundEditorTheme__textStrikethrough',
  underline: 'PlaygroundEditorTheme__textUnderline',
  both: 'PlaygroundEditorTheme__textUnderlineStrikethrough',
} as const;

function preserveTextDecorationFormats(root: HTMLElement) {
  const selector = [
    DECORATION_CLASS.strikethrough,
    DECORATION_CLASS.underline,
    DECORATION_CLASS.both,
  ]
    .map((c) => `span.${c}`)
    .join(', ');

  root.querySelectorAll<HTMLElement>(selector).forEach((el) => {
    const isBoth = el.classList.contains(DECORATION_CLASS.both);
    const needsStrike =
      isBoth || el.classList.contains(DECORATION_CLASS.strikethrough);
    const needsUnderline =
      isBoth || el.classList.contains(DECORATION_CLASS.underline);

    let current: HTMLElement = el;
    const wrapWith = (tag: 's' | 'u') => {
      const wrapper = el.ownerDocument.createElement(tag);
      current.replaceWith(wrapper);
      wrapper.appendChild(current);
      current = wrapper;
    };
    if (needsStrike) wrapWith('s');
    if (needsUnderline) wrapWith('u');
  });
}

// Ai Summary 결과 받아오는 hook
const useAiSummaries = ({ documentId }: { documentId: string }) => {
  const { isOwner } = useAi();
  const { getValues, setValue } = useFormContext<RecordFilter>();
  const { passportId } = useAtomValue(MyInfoState);

  const aiSummaries = useAtomValue(DocumentAiSummariesState);
  const updateAiSummaries = useSetAtom(DocumentAiSummariesState);

  const { mutate: updateAiSummaryData } = useTalkFlowDocumentAiSummary({
    documentId,
    passportId,
  });

  const eventTracker = useGTMEventTracker();
  const dialog = useDialog();
  const { showSuccess } = useSnackbar();
  const { t } = useTranslation();

  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  const deleteItem = useCallback(
    async (item: AiSummarizedContent) => {
      const deleteItem = async () => {
        const aiPipelineId = item.id;

        //로딩처리
        setDeletingIds((prev) => {
          const next = new Set(prev);
          next.add(aiPipelineId);
          return next;
        });

        try {
          await deleteTalkFlowDocumentAiSummary(documentId, aiPipelineId);
          await updateAiSummaryData();
          updateAiSummaries(
            Object.fromEntries(
              Object.entries(aiSummaries).filter(
                ([key]) => key !== aiPipelineId,
              ),
            ),
          );
        } catch (e) {
          dialog.showError({ e });
        } finally {
          setDeletingIds((prev) => {
            const next = new Set(prev);
            next.delete(aiPipelineId);
            return next;
          });
        }
      };

      const isGenerating = item.uiState === 'loading';
      const message = isGenerating
        ? t('AI 정리를 취소하시겠어요?')
        : `${t('이 정리 내용을 삭제하시겠어요?')}\n${t(
            '삭제 후에는 다시 볼 수 없어요.',
          )}`;

      const confirmButtonText = isGenerating ? t('실행 취소') : t('삭제');
      const cancelButtonText = isGenerating ? t('계속 하기') : t('취소');

      dialog.show({
        message,
        confirmButtonText,
        onConfirm: deleteItem,
        cancelButtonText,
      });
    },
    [aiSummaries, dialog, documentId],
  );

  const overWrite = useCallback(
    async (item: AiSummarizedContent) => {
      const hasContent =
        getValues('content')
          .replace(/<[^>]+>/g, '')
          .trim().length !== 0;

      const onConfirm = async () => {
        const aiPipelineId = item.id;
        setProcessingIds((prev) => new Set(prev).add(aiPipelineId));

        try {
          setValue('content', item.contentDom);
          // 약간의 딜레이를 주어 에디터 업데이트가 완료될 시간 확보
          await new Promise((resolve) => setTimeout(resolve, 100));
          showSuccess({ message: t('기존 내용을 덮어썼습니다.') });
        } finally {
          setProcessingIds((prev) => {
            const next = new Set(prev);
            next.delete(aiPipelineId);
            return next;
          });
        }
      };

      if (hasContent) {
        dialog.show({
          message: t(
            '덮어쓰기를 하면 작성해둔 내용이 모두 사라집니다. 덮어쓸까요?',
          ),
          cancelButtonText: t('취소'),
          confirmButtonText: t('확인'),
          onConfirm,
        });
        return;
      }

      await onConfirm();
    },
    [getValues, setValue, showSuccess, t, dialog],
  );

  const copyText = useCallback(async (item: AiSummarizedContent) => {
    await navigator.clipboard.writeText(removeMarkdown(item.content));
    showSuccess({ message: t('내용이 복사되었습니다.') });
  }, []);

  const addAiContent = useCallback(
    async (item: AiSummarizedContent) => {
      const aiPipelineId = item.id;
      setProcessingIds((prev) => new Set(prev).add(aiPipelineId));

      try {
        setValue('content', getValues('content') + item.contentDom);
        // 약간의 딜레이를 주어 에디터 업데이트가 완료될 시간 확보
        await new Promise((resolve) => setTimeout(resolve, 100));
        showSuccess({ message: t('작성 내용 아래에 추가했습니다.') });
      } finally {
        setProcessingIds((prev) => {
          const next = new Set(prev);
          next.delete(aiPipelineId);
          return next;
        });
      }
    },
    [getValues, setValue, showSuccess, t],
  );

  const sendGTMEvent = (
    type:
      | '작성내역 아래에 추가하기'
      | '덮어쓰기'
      | '전체 복사'
      | '정리내용 버리기',
  ) => {
    eventTracker(GTMEventName.clickPostAction, {
      page_area: getPrevPageArea(),
      area_name: '문서',
      action_type: type,
      ...getDocumentDetailParam(getValues()),
    });
  };

  const list = Object.entries(aiSummaries).map(([key, item]) => {
    const isProcessing = processingIds.has(key);
    const isDeleting = deletingIds.has(key);
    const isDisabled = isProcessing || isDeleting;

    return {
      id: key,
      state: item.uiState || 'idle',
      summary: item,
      deleteOnClick: () => deleteItem(item),
      actionList: [
        {
          tooltip: t('기존 내용 아래에 붙여넣기'),
          icon: <InsertToBottomIcon />,
          visible: isOwner,
          disabled: isDisabled,
          onClick: async () => {
            await addAiContent(item);
            sendGTMEvent('작성내역 아래에 추가하기');
          },
        },
        {
          tooltip: t('덮어쓰기'),
          icon: <ReplaceIcon />,
          visible: isOwner,
          disabled: isDisabled,
          onClick: async () => {
            await overWrite(item);
            sendGTMEvent('덮어쓰기');
          },
        },
        {
          tooltip: t('복사하기'),
          icon: <CopyIcon />,
          visible: true,
          disabled: isDisabled,
          onClick: async () => {
            await copyText(item);
            sendGTMEvent('전체 복사');
          },
        },
      ],
    };
  });

  return {
    deletingIds,
    processingIds,
    list,
  };
};

export default useAiSummaries;
